/**
 * @file Offline sync queue — stores failed pushes, replays on reconnect
 */

import { store, load, sbPullAll } from './store.js';
import { emit } from '@core/events.js';

let _online = navigator.onLine;
const QUEUE_KEY = 'offline_sync_queue';

/**
 * Check if the device is online.
 */
export function isOnline() {
  return _online;
}

/**
 * Add an item to the offline sync queue.
 */
export function queueSync(key, val) {
  const queue = load(QUEUE_KEY, []);
  queue.push({
    key,
    val,
    ts: Date.now(),
    retries: 0,
  });
  // Save directly to localStorage (don't trigger sbPush for the queue itself)
  try {
    localStorage.setItem('pbf_' + QUEUE_KEY, JSON.stringify(queue));
  } catch (e) {
    console.warn('[sync] Failed to save queue:', e.message);
  }
}

/**
 * Flush the offline queue — replay all pending writes.
 */
export async function flushSyncQueue() {
  if (!navigator.onLine) return;

  const queue = load(QUEUE_KEY, []);
  if (queue.length === 0) return;

  console.log(`[sync] Flushing ${queue.length} queued items`);
  const failed = [];

  for (const item of queue) {
    try {
      store(item.key, item.val);
    } catch (e) {
      item.retries++;
      if (item.retries < 5) {
        failed.push(item);
      }
    }
  }

  try {
    localStorage.setItem('pbf_' + QUEUE_KEY, JSON.stringify(failed));
  } catch (e) { /* best effort */ }

  if (failed.length > 0) {
    console.warn(`[sync] ${failed.length} items still in queue after flush`);
  }
  emit('sync:flushed', { succeeded: queue.length - failed.length, failed: failed.length });
}

/**
 * Initialize online/offline listeners.
 */
export function initSyncListeners() {
  window.addEventListener('online', () => {
    _online = true;
    emit('sync:online');
    console.log('[sync] Back online — flushing queue');
    setTimeout(() => {
      flushSyncQueue();
      sbPullAll();
    }, 1000);
  });

  window.addEventListener('offline', () => {
    _online = false;
    emit('sync:offline');
    console.log('[sync] Device went offline');
  });
}
